/**
 * Autocomplete matching for the combobox.
 *
 * Accent- and case-insensitive, because the Comunitat's names are full of
 * diacritics nobody will type: València, Alcoi, Sagunt/Sagunto, Castelló, and —
 * now that the same box also offers Catastro's street register — MARQUÈS DEL
 * TÚRIA. Requiring the accent would make the box useless for exactly the entries
 * most people are looking for.
 *
 * Moved here from map/lib when the study form needed a second combobox: the
 * rules were never about municipalities, and a copy under survey/ would have
 * been a second set of accent rules to keep in step.
 */

/** Casefolded and stripped of diacritics, for comparison only. */
export function normalize(value: string): string {
  return value
    .normalize("NFD")
    // Combining diacritical marks. Applied after NFD has split "é" into "e" plus
    // its accent, so this drops the accent and keeps the letter.
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Options matching a query, prefix matches first.
 *
 * Prefix before substring because someone typing "val" wants València, not the
 * municipalities with "val" somewhere in the middle. Within each group the
 * source order is preserved, which is the backend's alphabetical order for
 * municipalities and Catastro's own order for streets.
 */
export function searchOptions(
  options: readonly string[],
  query: string,
  limit = 8,
): string[] {
  const q = normalize(query);
  if (!q) return [];

  const prefix: string[] = [];
  const contains: string[] = [];

  for (const name of options) {
    const n = normalize(name);
    if (n.startsWith(q)) prefix.push(name);
    else if (n.includes(q)) contains.push(name);
  }

  return [...prefix, ...contains].slice(0, limit);
}

export interface MatchPart {
  text: string;
  hit: boolean;
}

/**
 * A name split into matched and unmatched runs, for highlighting.
 *
 * Returns parts rather than HTML: the legacy page built a string with <mark>
 * around the match and assigned it to innerHTML, which puts a municipality name
 * through the parser. Returning data and letting React render it removes that
 * path entirely.
 */
export function highlightMatch(name: string, query: string): MatchPart[] {
  const q = normalize(query);
  if (!q) return [{ text: name, hit: false }];

  // Indices come from the normalized form, and NFD can change the length. Slice
  // the original by the same index only when the two agree; otherwise fall back
  // to no highlight — a missing highlight is invisible, a misaligned one
  // corrupts the name on screen.
  const n = normalize(name);
  if (n.length !== name.length) return [{ text: name, hit: false }];

  const at = n.indexOf(q);
  if (at < 0) return [{ text: name, hit: false }];

  const parts: MatchPart[] = [];
  if (at > 0) parts.push({ text: name.slice(0, at), hit: false });
  parts.push({ text: name.slice(at, at + q.length), hit: true });
  if (at + q.length < name.length) {
    parts.push({ text: name.slice(at + q.length), hit: false });
  }
  return parts;
}
